import { useCallback, useId, useRef, useState } from 'react'
import { AlertTriangle, FileSpreadsheet, Upload } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { m } from '@/lib/paraglide/messages'

interface CustomerImportDialogProps {
    /** Người dùng đã chọn file và bấm "Bắt đầu import". */
    onImport: (params: { file: File; syncDebt: boolean }) => void
    isPending: boolean
}

/**
 * Cổng xác nhận trước khi import khách hàng từ Excel.
 *
 * Lý do phải có bước này thay vì mở thẳng hộp chọn file: import có thể ghi đè
 * công nợ của khách đã tồn tại — thao tác không tự hoàn tác được. Công tắc
 * "đồng bộ công nợ" mặc định TẮT, và chỉ khi bật mới hiện cảnh báo, để người
 * bấm nhầm không vô tình kéo số dư của toàn bộ khách về mốc trong file.
 */
export function CustomerImportDialog({
    onImport,
    isPending,
}: CustomerImportDialogProps) {
    const [open, setOpen] = useState(false)
    const [file, setFile] = useState<File | null>(null)
    const [syncDebt, setSyncDebt] = useState(false)
    const [fileError, setFileError] = useState<string | null>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)
    const syncDebtId = useId()
    const warningId = useId()
    const fileErrorId = useId()

    // Mỗi lần mở lại là một lượt import mới: quên file và tắt lại công tắc, để
    // lựa chọn nguy hiểm của lần trước không âm thầm áp dụng cho lần này.
    const handleOpenChange = useCallback((next: boolean) => {
        setOpen(next)
        if (!next) {
            setFile(null)
            setSyncDebt(false)
            setFileError(null)
        }
    }, [])

    const handleSubmit = useCallback(() => {
        // Nút submit không bị khoá khi thiếu file — bấm được, rồi báo lỗi ngay
        // cạnh trường sai, để người dùng biết vì sao chưa import được.
        if (!file) {
            setFileError(m.import_file_required())
            fileInputRef.current?.focus()
            return
        }
        onImport({ file, syncDebt })
        handleOpenChange(false)
    }, [file, syncDebt, onImport, handleOpenChange])

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogTrigger asChild>
                <Button size="sm" variant="outline" disabled={isPending}>
                    <Upload className="size-4" />
                    {isPending ? m.import_importing() : m.import_excel()}
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>{m.import_dialog_title()}</DialogTitle>
                    <DialogDescription>
                        {m.import_dialog_description()}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="customer-import-file">
                            {m.import_choose_file()}
                        </Label>
                        <input
                            ref={fileInputRef}
                            id="customer-import-file"
                            type="file"
                            accept=".xlsx"
                            className="sr-only"
                            aria-invalid={fileError !== null}
                            aria-describedby={
                                fileError ? fileErrorId : undefined
                            }
                            onChange={(e) => {
                                setFile(e.target.files?.[0] ?? null)
                                setFileError(null)
                            }}
                        />
                        <div className="flex items-center gap-2">
                            <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <FileSpreadsheet className="size-4" />
                                {m.import_choose_file()}
                            </Button>
                            <span
                                className="min-w-0 flex-1 truncate text-sm text-muted-foreground"
                                title={file?.name}
                            >
                                {file?.name ?? m.import_no_file_selected()}
                            </span>
                        </div>
                        {fileError && (
                            <p
                                id={fileErrorId}
                                role="alert"
                                className="text-xs text-destructive"
                            >
                                {fileError}
                            </p>
                        )}
                    </div>

                    <div className="space-y-2 rounded-md border p-3">
                        <div className="flex items-start gap-2">
                            <input
                                id={syncDebtId}
                                type="checkbox"
                                className="mt-0.5 size-4 shrink-0 cursor-pointer accent-destructive"
                                checked={syncDebt}
                                aria-describedby={
                                    syncDebt ? warningId : undefined
                                }
                                onChange={(e) => setSyncDebt(e.target.checked)}
                            />
                            <Label
                                htmlFor={syncDebtId}
                                className="cursor-pointer leading-snug"
                            >
                                {m.import_sync_debt_label()}
                            </Label>
                        </div>
                        <p className="pl-6 text-xs text-muted-foreground">
                            {m.import_sync_debt_hint()}
                        </p>
                        {syncDebt && (
                            <p
                                id={warningId}
                                role="alert"
                                className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive"
                            >
                                <AlertTriangle className="mt-px size-4 shrink-0" />
                                <span>{m.import_sync_debt_warning()}</span>
                            </p>
                        )}
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => handleOpenChange(false)}
                    >
                        {m.common_cancel()}
                    </Button>
                    {/* Không disable theo trạng thái hợp lệ của form: chỉ chặn
                        khi request đang bay để tránh double-submit. */}
                    <Button
                        type="button"
                        variant={syncDebt ? 'destructive' : 'default'}
                        onClick={handleSubmit}
                        disabled={isPending}
                    >
                        {m.import_start()}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
